
import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useCentralizedAuth } from '@/context/CentralizedAuthProvider';
import LoadingScreen from '@/components/assessment/LoadingScreen';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading } = useCentralizedAuth();
  const [isReady, setIsReady] = useState(false);
  const location = useLocation();
  
  // Set ready state after auth state is loaded
  useEffect(() => {
    if (!isLoading) {
      // Small delay to ensure all auth processes are complete
      const timer = setTimeout(() => {
        setIsReady(true);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  // If still loading, show loading screen
  if (isLoading || !isReady) {
    return <LoadingScreen message="Verifying your session..." />;
  }
  
  // If authenticated, render children
  if (isAuthenticated) {
    return <>{children}</>;
  }
  
  // If not authenticated, redirect to login with current path
  const currentPath = location.pathname + location.search;
  return <Navigate 
    to={`/login?redirect=${encodeURIComponent(currentPath)}`} 
    replace 
  />;
};

export default ProtectedRoute;
